import { LockSimpleIcon } from "@phosphor-icons/react";
import { useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { ContinuousA4Preview } from "@/components/scheduled-briefings/template-studio/ContinuousA4Preview";
import { Button } from "@/components/ui/button";
import EditableText from "@/components/ui/editable-text/EditableText";
import RecipientsListSection from "@/components/ui/RecipientsListSection";
import { REPORT_HIGHLIGHT_DEFAULT_TITLE } from "@/components/ui/report/ReportHighlight/ReportHighlight";
import { A4_PREVIEW_WIDTH_PX } from "@/components/ui/report/TemplateStudioEmptyCanvas/TemplateStudioEmptyCanvas";
import { JollySelect, SelectItem } from "@/components/ui/select";
import { JollyTextField } from "@/components/ui/textfield";
import {
  applyBriefingImageAssetsToDisplaySpec,
  applyBriefingNameToDisplaySpec,
  applyBriefingSlotInputsToDisplaySpec,
  applyBriefingSlotTitlesToDisplaySpec,
  applySampleOutputCaptionToDisplaySpec,
  getDisplaySpecFromDraft,
  getMarkdownHeadlineLine,
  getTemplateBriefingTitle,
  getTemplateFooterText,
} from "@/lib/template-studio/draft/templateStudioDraft";
import { resolveReportThemeFromLayoutTheme } from "@/lib/template-studio/reportThemeFromLayoutTheme";
import type { UseTemplateLayoutReturn } from "@/lib/template-studio/repository/useTemplateLayout";
import { formatTemplateStudioComponentLabel } from "@/lib/template-studio/templateStudioComponentDisplay";
import { cn } from "@/lib/utils";
import useBriefingCreateStore, {
  selectRecipientsError,
} from "@/store/scheduled-briefings/briefing-create/briefing-create.slice";
import type {
  TemplateLayoutDocument,
  TemplateSlot,
} from "@/types/template-layout/templateLayout.types";
import BriefingCreateScheduleSection from "./BriefingCreateScheduleSection";
import { BriefingImageAssetsSection } from "./BriefingImageAssetsSection";

const LOOKBACK_OPTIONS = [
  { id: "1", label: "Last 24 hours" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
];

const BRIEFING_CREATE_RECIPIENTS_HELPER =
  "Each recipient will receive the edition once it is approved and sent.";

/** Valid recipient chip — Figma node 76:9512 (blue-50 / blue-700 tokens) */
const BRIEFING_CREATE_RECIPIENT_CLASS_NAMES = {
  validChip: "rounded-sm border border-blue-50 bg-blue-50",
  validChipLabel: "font-normal leading-4 tracking-[0.12px] text-blue-700",
  validChipRemoveButton: "text-blue-700 hover:text-blue-800",
  invalidChip:
    "rounded-sm border border-red-50 bg-red-50 tracking-[0.12px] text-red-700",
  invalidChipRemoveButton: "text-red-700 hover:text-red-800",
};

interface BriefingConfigureSidebarSectionTitleProps {
  children: string;
  className?: string;
}

const BriefingConfigureSidebarSectionTitle = ({
  children,
  className,
}: BriefingConfigureSidebarSectionTitleProps) => (
  <h2
    className={cn(
      "text-xs font-medium uppercase leading-4 tracking-[0.48px] text-gray-600",
      className,
    )}
  >
    {children}
  </h2>
);

export const getSlotsInLayoutOrder = ({
  slots,
  spec,
}: TemplateLayoutDocument): TemplateSlot[] => {
  const slotsById = new Map(slots.map((slot) => [slot.id, slot]));
  const orderedSlots: TemplateSlot[] = [];
  const visitedElementIds = new Set<string>();

  const visitElement = (elementId: string) => {
    if (visitedElementIds.has(elementId)) return;
    visitedElementIds.add(elementId);

    const slot = slotsById.get(elementId);
    if (slot) orderedSlots.push(slot);

    const element = spec.elements[elementId];
    if (!element) return;

    for (const childId of element.children ?? []) visitElement(childId);
    for (const childIds of Object.values(element.slots ?? {})) {
      for (const childId of childIds) visitElement(childId);
    }
  };

  visitElement(spec.root);
  return orderedSlots;
};

/** Trimmed `title` prop saved on the template element, or "" when it has none. */
const getTemplateTitle = (
  slot: TemplateSlot,
  document: TemplateLayoutDocument,
): string => {
  const title = document.spec.elements[slot.id]?.props?.title;
  return typeof title === "string" ? title.trim() : "";
};

/** Slot label, or `null` for a Markdown text block without an H1/H2 headline. */
const getSlotDisplayLabel = (
  slot: TemplateSlot,
  document: TemplateLayoutDocument,
): string | null => {
  const props = document.spec.elements[slot.id]?.props;
  const title = getTemplateTitle(slot, document);
  if (title) return title;
  if (slot.contentType === "ReportHighlight") {
    return REPORT_HIGHLIGHT_DEFAULT_TITLE;
  }
  if (slot.contentType === "Markdown") {
    const headline = getMarkdownHeadlineLine(props?.content);
    return headline ? headline.replace(/^#{1,2}\s+/, "").trim() : null;
  }

  const persistedLabel = slot.label.trim();
  if (persistedLabel && persistedLabel !== slot.id) return persistedLabel;

  return formatTemplateStudioComponentLabel(slot.contentType);
};

const getSlotInputPlaceholder = (slot: TemplateSlot, label: string): string => {
  if (slot.inputMode === "text") {
    return slot.contentType === "ReportFooter"
      ? "Enter the footer text to display..."
      : "Enter the text to display...";
  }
  if (slot.contentType === "ReportHighlight") {
    return `Enter a prompt for ${label}, such as what the report includes and what readers need to know...`;
  }
  if (slot.contentType === "ReportSection") {
    return `Enter a prompt for ${label}, such as the articles to include, topics, sources, and focus...`;
  }
  if (
    slot.contentType.endsWith("Chart") ||
    slot.contentType === "HorizontalBar"
  ) {
    return `Enter a prompt for ${label}, such as the data and comparison this chart should show...`;
  }
  return `Enter a prompt for ${label}, such as the content, data, or key points to include...`;
};

/** Header, Date, and Image blocks render from the template as-is, so they take no prompt. */
const isConfigurableSlot = (slot: TemplateSlot): boolean =>
  slot.contentType !== "ReportHeader" &&
  slot.contentType !== "ReportDate" &&
  slot.contentType !== "Image";

export interface ConfigureBriefingViewProps {
  /** Owned by the parent so the save action can read the same template. */
  templateLayout: UseTemplateLayoutReturn;
  /** The user can't edit this briefing (`canEdit: false`); every input is disabled. */
  isReadOnly?: boolean;
}

export const ConfigureBriefingView = ({
  templateLayout,
  isReadOnly = false,
}: ConfigureBriefingViewProps) => {
  const { template, isLoading, error, isRetryable, retry } = templateLayout;
  const editedBriefingName = useBriefingCreateStore(
    (state) => state.briefingName,
  );
  const briefingName =
    editedBriefingName ??
    (template ? getTemplateBriefingTitle(template.document) : "");
  const overallPrompt = useBriefingCreateStore((state) => state.overallPrompt);
  const lookbackDays = useBriefingCreateStore((state) => state.lookbackDays);
  const slotInputs = useBriefingCreateStore((state) => state.slotInputs);
  const slotTitles = useBriefingCreateStore((state) => state.slotTitles);
  const imageAssets = useBriefingCreateStore((state) => state.imageAssets);
  const footerSlotIds = useMemo(
    () =>
      template
        ? getSlotsInLayoutOrder(template.document)
            .filter((slot) => slot.contentType === "ReportFooter")
            .map((slot) => slot.id)
        : [],
    [template],
  );
  const footerSlotInputs = useBriefingCreateStore(
    useShallow((state) =>
      Object.fromEntries(
        footerSlotIds.flatMap((slotId) => {
          const input = state.slotInputs[slotId];
          return input !== undefined ? [[slotId, input]] : [];
        }),
      ),
    ),
  );
  const basePreviewSpec = useMemo(
    () =>
      template
        ? applySampleOutputCaptionToDisplaySpec(
            getDisplaySpecFromDraft(template.document),
          )
        : null,
    [template],
  );
  const previewSpec = useMemo(
    () =>
      basePreviewSpec
        ? applyBriefingNameToDisplaySpec(
            applyBriefingImageAssetsToDisplaySpec(
              applyBriefingSlotTitlesToDisplaySpec(
                applyBriefingSlotInputsToDisplaySpec(
                  basePreviewSpec,
                  footerSlotInputs,
                ),
                slotTitles,
              ),
              imageAssets,
            ),
            briefingName,
          )
        : null,
    [basePreviewSpec, footerSlotInputs, slotTitles, imageAssets, briefingName],
  );
  const previewTheme = useMemo(
    () =>
      template
        ? resolveReportThemeFromLayoutTheme(template.document.theme)
        : null,
    [template],
  );
  const setBriefingName = useBriefingCreateStore(
    (state) => state.setBriefingName,
  );
  const setOverallPrompt = useBriefingCreateStore(
    (state) => state.setOverallPrompt,
  );
  const setLookbackDays = useBriefingCreateStore(
    (state) => state.setLookbackDays,
  );
  const setSlotInput = useBriefingCreateStore((state) => state.setSlotInput);
  const setSlotTitle = useBriefingCreateStore((state) => state.setSlotTitle);
  const seedSlotInputs = useBriefingCreateStore(
    (state) => state.seedSlotInputs,
  );
  useEffect(() => {
    if (!template) return;
    seedSlotInputs(
      Object.fromEntries(
        footerSlotIds.map((slotId) => [
          slotId,
          getTemplateFooterText(template.document, slotId),
        ]),
      ),
    );
  }, [template, footerSlotIds, seedSlotInputs]);
  const scheduleFrequency = useBriefingCreateStore(
    (state) => state.scheduleFrequency,
  );
  const scheduleTime = useBriefingCreateStore((state) => state.scheduleTime);
  const timezone = useBriefingCreateStore((state) => state.timezone);
  const setScheduleFrequency = useBriefingCreateStore(
    (state) => state.setScheduleFrequency,
  );
  const setScheduleTime = useBriefingCreateStore(
    (state) => state.setScheduleTime,
  );
  const setTimezone = useBriefingCreateStore((state) => state.setTimezone);
  const seedTimezone = useBriefingCreateStore((state) => state.seedTimezone);
  const validRecipientEmails = useBriefingCreateStore(
    (state) => state.validRecipientEmails,
  );
  const invalidRecipientEmails = useBriefingCreateStore(
    (state) => state.invalidRecipientEmails,
  );
  const recipientsError = useBriefingCreateStore(selectRecipientsError);
  const recipientInputValue = useBriefingCreateStore(
    (state) => state.recipientInputValue,
  );
  const setRecipientInputValue = useBriefingCreateStore(
    (state) => state.setRecipientInputValue,
  );
  const commitRecipientInput = useBriefingCreateStore(
    (state) => state.commitRecipientInput,
  );
  const removeValidRecipient = useBriefingCreateStore(
    (state) => state.removeValidRecipient,
  );
  const removeInvalidRecipient = useBriefingCreateStore(
    (state) => state.removeInvalidRecipient,
  );
  const setInvalidRecipientValue = useBriefingCreateStore(
    (state) => state.setInvalidRecipientValue,
  );
  const commitInvalidRecipient = useBriefingCreateStore(
    (state) => state.commitInvalidRecipient,
  );
  const imageSlotIds = template
    ? getSlotsInLayoutOrder(template.document)
        .filter((slot) => slot.contentType === "Image")
        .map((slot) => slot.id)
    : [];
  let textBlockCount = 0;
  const inputSlots = template
    ? getSlotsInLayoutOrder(template.document)
        .filter(isConfigurableSlot)
        .map((slot) => {
          const label = getSlotDisplayLabel(slot, template.document);
          if (!label) {
            textBlockCount += 1;
            return {
              slot,
              label: `Text block ${textBlockCount}`,
              templateTitle: "",
              isRenamable: false,
            };
          }
          // Only a titled section has a heading to rename.
          const templateTitle =
            slot.contentType === "ReportSection"
              ? getTemplateTitle(slot, template.document)
              : "";
          return {
            slot,
            label: (templateTitle && slotTitles[slot.id]) || label,
            templateTitle,
            isRenamable: Boolean(templateTitle) && !isReadOnly,
          };
        })
    : [];

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden bg-[#fafafa]">
      <aside className="flex w-125 shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-200 hover:scrollbar-thumb-slate-300 [&_label]:font-medium [&_label]:text-gray-700">
        <section className="border-b border-slate-200 px-6 pt-7 pb-6">
          <div className="flex items-start justify-between gap-4">
            <BriefingConfigureSidebarSectionTitle>
              Basics
            </BriefingConfigureSidebarSectionTitle>
            <div className="flex shrink-0 items-center gap-1.5 text-xs leading-4 text-gray-600">
              <LockSimpleIcon aria-hidden className="size-3.5" />
              <span>Layout locked</span>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-4">
            <JollyTextField
              label="Briefing name"
              labelVariant="secondary"
              placeholder="Type name..."
              value={briefingName}
              onChange={setBriefingName}
              isDisabled={isReadOnly}
              clearable={false}
              className="rounded-sm text-sm"
            />
            <JollyTextField
              label="Overall prompt"
              labelVariant="secondary"
              placeholder="Type prompt..."
              value={overallPrompt}
              onChange={setOverallPrompt}
              isDisabled={isReadOnly}
              textArea
              rows={1}
              maxRows={10}
              clearable={false}
              className="rounded-sm text-sm"
            />
          </div>
        </section>

        {template && imageSlotIds.length > 0 ? (
          <section className="border-b border-slate-200 px-6 py-6">
            <BriefingConfigureSidebarSectionTitle>
              Image
            </BriefingConfigureSidebarSectionTitle>
            <div className="mt-4">
              <BriefingImageAssetsSection
                document={template.document}
                imageSlotIds={imageSlotIds}
                isDisabled={isReadOnly}
              />
            </div>
          </section>
        ) : null}

        <section className="border-b border-slate-200 px-6 py-6">
          <BriefingConfigureSidebarSectionTitle>
            Lookback
          </BriefingConfigureSidebarSectionTitle>
          <div className="mt-3.5">
            <JollySelect
              aria-label="Lookback"
              placeholder="Select"
              size="sm"
              items={LOOKBACK_OPTIONS}
              selectedKey={String(lookbackDays)}
              onSelectionChange={(key) => setLookbackDays(Number(key))}
              isDisabled={isReadOnly}
              className="[&_button]:h-9 [&_button]:rounded-sm [&_button]:text-sm"
            >
              {(option) => (
                <SelectItem id={option.id}>{option.label}</SelectItem>
              )}
            </JollySelect>
          </div>
        </section>

        {inputSlots.length > 0 ? (
          <section className="border-b border-slate-200 px-6 py-6">
            <BriefingConfigureSidebarSectionTitle>
              Section prompts
            </BriefingConfigureSidebarSectionTitle>
            {/* Labels that can't be renamed indent like the rename box text (1px border + 12px padding). */}
            <div className="mt-4 flex flex-col gap-4 [&_label]:pl-[13px]">
              {inputSlots.map(({ slot, label, templateTitle, isRenamable }) => (
                <div key={slot.id} className="flex flex-col">
                  {isRenamable ? (
                    // The box sits flush with the prompt field's left edge (Figma 616:92843).
                    <div className="mb-1.5 flex min-w-0">
                      <EditableText
                        value={label}
                        // Typing the template title back clears the rename.
                        onSave={(title) =>
                          setSlotTitle(
                            slot.id,
                            title === templateTitle ? "" : title,
                          )
                        }
                        allowEmpty
                        align="box"
                        label="Section name"
                        tooltip="Rename"
                        // Figma: the tooltip overlaps the box bottom and starts near its right end.
                        tooltipProps={{
                          placement: "bottom end",
                          offset: -6,
                          crossOffset: 42,
                          className:
                            "bg-gray-800 text-gray-50 tracking-[0.12px]",
                        }}
                        className="font-medium leading-5 tracking-[0.12px] text-gray-700 data-hovered:border-slate-200 data-hovered:bg-white"
                      />
                    </div>
                  ) : null}
                  <JollyTextField
                    {...(isRenamable ? { "aria-label": label } : { label })}
                    labelVariant="secondary"
                    placeholder={getSlotInputPlaceholder(slot, label)}
                    value={slotInputs[slot.id] ?? ""}
                    onChange={(value) => setSlotInput(slot.id, value)}
                    isDisabled={isReadOnly}
                    textArea
                    rows={slot.contentType === "ReportHighlight" ? 2 : 1}
                    maxRows={10}
                    clearable={false}
                    className="rounded-sm text-sm"
                  />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="border-b border-slate-200 px-6 py-6">
          <BriefingConfigureSidebarSectionTitle>
            Schedule
          </BriefingConfigureSidebarSectionTitle>
          <div className="mt-4">
            <BriefingCreateScheduleSection
              scheduleFrequency={scheduleFrequency}
              scheduleTime={scheduleTime}
              timezone={timezone}
              onScheduleFrequencyChange={setScheduleFrequency}
              onScheduleTimeChange={setScheduleTime}
              onTimezoneChange={setTimezone}
              onTimezoneAutoSelect={seedTimezone}
              isDisabled={isReadOnly}
            />
          </div>
        </section>

        <section className="px-6 py-6">
          <BriefingConfigureSidebarSectionTitle>
            Recipients
          </BriefingConfigureSidebarSectionTitle>
          <p className="text-sm leading-5 text-gray-600 py-2">
            {BRIEFING_CREATE_RECIPIENTS_HELPER}
          </p>
          <div className="mt-2 flex flex-col gap-4">
            <RecipientsListSection
              label="Recipient emails"
              labelVariant="secondary"
              labelSize="md"
              placeholder="Add recipient email..."
              containerClassName="min-h-[5.5rem] items-start content-start rounded-sm px-3 py-2 gap-2"
              classNames={BRIEFING_CREATE_RECIPIENT_CLASS_NAMES}
              validEmails={validRecipientEmails}
              invalidEmails={invalidRecipientEmails}
              recipientsError={isReadOnly ? undefined : recipientsError}
              emptyHint={
                isReadOnly
                  ? undefined
                  : "No one will receive this briefing until you add recipients."
              }
              inputValue={recipientInputValue}
              onInputChange={setRecipientInputValue}
              onCommitInput={commitRecipientInput}
              onRemoveValidEmail={removeValidRecipient}
              onRemoveInvalidEmail={removeInvalidRecipient}
              onInvalidEmailChange={setInvalidRecipientValue}
              onInvalidEmailCommit={commitInvalidRecipient}
              isDisabled={isReadOnly}
            />
          </div>
        </section>
      </aside>

      <section
        aria-label="Template preview"
        className="flex min-w-0 flex-1 flex-col overflow-hidden bg-[#fafafa]"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-10 py-8 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-200 hover:scrollbar-thumb-slate-300">
          <div
            className="mx-auto flex w-full flex-col gap-3"
            style={{ maxWidth: A4_PREVIEW_WIDTH_PX }}
          >
            <p className="text-[11px] font-semibold tracking-[0.44px] text-gray-600 uppercase">
              Preview
            </p>
            {isLoading ? (
              <p className="text-sm text-gray-600">Loading template…</p>
            ) : null}
            {error ? (
              <div className="flex flex-col items-start gap-3" role="alert">
                <p className="text-sm text-red-600">{error.message}</p>
                {isRetryable ? (
                  <Button variant="outline" size="sm" onPress={retry}>
                    Try again
                  </Button>
                ) : null}
              </div>
            ) : null}
            {!isLoading && !error && previewSpec && previewTheme ? (
              <div className="relative min-h-0 w-full">
                <ContinuousA4Preview spec={previewSpec} theme={previewTheme} />
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
};
